/**
 * usePortalLayout Hook
 * Orchestrates footer data fetching via mediator + sidebar state from store.
 * Pages using <PortalLayout> get layout state for free via this hook.
 */

import { useCallback, useEffect } from 'react';
import { logger, mediator } from '@/application/core';
import {
  GetPortalLayoutQuery,
  type PortalLayoutResponse,
} from '@/application/queries/portalLayout';
import {
  usePortalLayoutFooterData,
  usePortalLayoutSidebarOpen,
  usePortalLayoutLoading,
  usePortalLayoutError,
  useSetPortalLayoutFooterData,
  useSetPortalLayoutLoading,
  useSetPortalLayoutError,
  useSetPortalLayoutSidebarOpen,
  usePortalLayoutToggleSidebar,
} from './PortalLayout.store';
import { useIsMobile } from '@components/hooks/use-mobile';

/**
 * Hook return type
 */
interface UsePortalLayoutResult {
  footerData: PortalLayoutResponse | null;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  isMobile: boolean;
}

/**
 * Hook to fetch portal layout data and provide sidebar state
 */
export function usePortalLayout(): UsePortalLayoutResult {
  const footerData = usePortalLayoutFooterData();
  const sidebarOpen = usePortalLayoutSidebarOpen();
  const loading = usePortalLayoutLoading();
  const error = usePortalLayoutError();
  const setFooterData = useSetPortalLayoutFooterData();
  const setLoading = useSetPortalLayoutLoading();
  const setError = useSetPortalLayoutError();
  const setSidebarOpen = useSetPortalLayoutSidebarOpen();
  const toggleSidebar = usePortalLayoutToggleSidebar();
  const isMobile = useIsMobile();

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const result = await mediator.send<PortalLayoutResponse>(new GetPortalLayoutQuery());
      setFooterData(result);
    } catch (err) {
      const fetchError =
        err instanceof Error ? err : new Error('Failed to fetch portal layout data');
      setError(fetchError);
      logger.error('usePortalLayout', 'Error:', err);
    }
  }, [setFooterData, setLoading, setError]);

  useEffect(() => {
    // Only fetch if we don't have data yet
    if (!footerData) {
      fetchData();
    }
  }, [footerData, fetchData]);

  // Mobile-aware sidebar initialization: auto-close on mobile viewport
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile, setSidebarOpen]);

  return {
    footerData,
    sidebarOpen,
    toggleSidebar,
    loading,
    error,
    refetch: fetchData,
    isMobile,
  };
}
